import React, { useEffect, useState } from 'react';
import { ArrowUpRight, Play } from 'lucide-react';
import { TOOL_ICONS } from '../../workspace/lib/toolIcons';

/**
 * ShortlistStartersSection
 *
 * Two-column section behind a BuildPilot Intelligence drift canvas.
 *
 *   Left (lg+):  rotating "Shortlist a {type} in 2 minutes" heading +
 *                6 mini-matcher pill entry points stacked in two groups.
 *   Right (lg+): "What is BuildPilot Intelligence?" eyebrow + brand video
 *                (buildpilot-commercial.mp4) in a click-to-play card.
 *
 * On mobile the two columns stack: heading → pills → video.
 *
 * Mini Matcher dispatch (consumed by the Header-mounted matchers):
 *   - buildpilot:open-matcher              (Builders / Trades / Suppliers / Pros)
 *   - buildpilot:open-hnl-matcher          (Home & Land)
 *   - buildpilot:open-home-designs-matcher (Floor Plans)
 */

// Mini Matcher category options - copied 1:1 from Header.jsx.
const BUILDER_CATEGORY_OPTIONS = [
  { label: 'New homes',                category: 'professional:builder',                  iconSrc: '/images/icons/new-homes.avif',            prefill: { build_type: 'New Homes' } },
  { label: 'Renovations',              category: 'professional:builder',                  iconSrc: '/images/icons/renovations.avif',          prefill: { build_type: 'Renovations' } },
  { label: 'Verandahs or pergolas',    category: 'professional:home_improvement_builder', iconSrc: '/images/icons/verandahs-pergolas.avif',   prefill: { build_type: 'Pergolas/Verandahs' } },
  { label: 'Landscaping',              category: 'tradie:landscaper',                     iconSrc: '/images/icons/landscaping.avif',          prefill: { build_type: 'Landscaping' } },
  { label: 'Sheds or garages',         category: 'professional:home_improvement_builder', iconSrc: '/images/icons/sheds-garages.avif',        prefill: { build_type: 'Sheds/Garages' } },
  { label: 'Decking',                  category: 'professional:home_improvement_builder', iconSrc: '/images/icons/decking.avif',              prefill: { build_type: 'Decking' } },
  // 2026-02-20: 'Interior design' and 'Home design' chips removed.
  // Interior designers and building designers are professionals, not
  // builders - they're now shortlisted via the standalone Find a
  // Professional tool. Surfacing them inside the builder shortlist
  // confused users into thinking they were getting a builder match.
  { label: 'Swimming pools',           category: 'professional:home_improvement_builder', iconSrc: '/images/icons/swimming-pools.avif',       prefill: { build_type: 'Swimming Pools' } },
  { label: 'Choosing a block of land', category: 'professional:builder',                  iconSrc: '/images/icons/choosing-land.avif',        prefill: { build_type: 'New Homes' } },
  { label: 'Changing builders',        category: 'professional:builder',                  iconSrc: '/images/icons/changing-builders.avif',    prefill: { build_type: 'New Homes' } },
  { label: 'Invest or develop',        category: 'professional:builder',                  iconSrc: '/images/icons/property-development.avif', prefill: { build_type: 'New Homes' } },
];

const MATCHER_ICON = (slug) => `/images/toolbox-icons/matcher/${slug}.png`;

const TRADE_CATEGORY_OPTIONS = [
  { label: 'Electrician',     category: 'tradie:electrician',  iconSrc: MATCHER_ICON('electrician'),  blurb: 'Wiring, power points, lights, EV chargers' },
  { label: 'Plumber',         category: 'tradie:plumber',      iconSrc: MATCHER_ICON('plumber'),      blurb: 'Bathrooms, hot water, gas, drains' },
  { label: 'Painter',         category: 'tradie:painter',      iconSrc: MATCHER_ICON('painter'),      blurb: 'Fresh coats inside and out, repaints and new builds' },
  { label: 'Carpenter',       category: 'tradie:carpenter',    iconSrc: MATCHER_ICON('carpenter'),    blurb: 'Framing, decks, doors, built-ins' },
  { label: 'Tiler',           category: 'tradie:tiler',        iconSrc: MATCHER_ICON('tiler'),        blurb: 'Bathrooms, kitchens, splashbacks and wet areas' },
  { label: 'Concreter',       category: 'tradie:concreter',    iconSrc: MATCHER_ICON('concreter'),    blurb: 'Slabs, driveways, footings' },
  { label: 'Bricklayer',      category: 'tradie:bricklayer',   iconSrc: MATCHER_ICON('bricklayer'),   blurb: 'Brick and block walls, retaining, fences, features' },
  { label: 'Plasterer',       category: 'tradie:plasterer',    iconSrc: MATCHER_ICON('plasterer'),    blurb: 'Walls, ceilings, cornices and render' },
  { label: 'Roofer',          category: 'tradie:roofer',       iconSrc: MATCHER_ICON('roofer'),       blurb: 'New roofs, replacements, repairs, gutters' },
  { label: 'Landscaper',      category: 'tradie:landscaper',   iconSrc: MATCHER_ICON('landscaper'),   blurb: 'Lawns, gardens, paving and watering systems' },
  { label: 'Fencing & gates', category: 'tradie:fencer',       iconSrc: MATCHER_ICON('fencing'),      blurb: 'Boundary, pool, side gates and automatic gates' },
];

const SUPPLIER_CATEGORY_OPTIONS = [
  { label: 'Kitchens & appliances', category: 'supplier:appliances',         iconSrc: MATCHER_ICON('kitchens'),         blurb: 'Where the cooking happens - cupboards, ovens, fridges' },
  { label: 'Bathrooms & tapware',   category: 'supplier:tapware',            iconSrc: MATCHER_ICON('bathrooms'),        blurb: 'Vanities, basins, toilets, taps, showers' },
  { label: 'Tiles',                 category: 'supplier:tiles',              iconSrc: MATCHER_ICON('tiles'),            blurb: 'For walls, floors, splashbacks and wet areas' },
  { label: 'Flooring',              category: 'supplier:flooring',           iconSrc: MATCHER_ICON('flooring'),         blurb: 'Timber, vinyl, carpet - what you walk on every day' },
  { label: 'Stone & benchtops',     category: 'supplier:stone_benchtops',    iconSrc: MATCHER_ICON('stone-benchtops'),  blurb: 'Kitchen, vanity and laundry tops' },
  { label: 'Concrete & paving',     category: 'supplier:building_materials', iconSrc: MATCHER_ICON('concrete-paving'),  blurb: 'Driveways, pathways, slabs' },
  { label: 'Windows & doors',       category: 'supplier:windows_doors',      iconSrc: MATCHER_ICON('windows-doors'),    blurb: 'Front doors, sliders, glass - your home\u2019s face' },
  { label: 'Lighting',              category: 'supplier:lighting',           iconSrc: MATCHER_ICON('lighting'),         blurb: 'Lights you\u2019ll see day-to-day, plus fans and switches' },
  { label: 'Solar & batteries',     category: 'supplier:solar_products',     iconSrc: MATCHER_ICON('solar-batteries'),  blurb: 'Knock the power bill down - panels and batteries' },
  { label: 'Roofing',               category: 'supplier:roofing_products',   iconSrc: MATCHER_ICON('roofing'),          blurb: 'The lid on the house - Colorbond, tiles, sheets' },
  { label: 'Cladding',              category: 'supplier:cladding',           iconSrc: MATCHER_ICON('cladding'),         blurb: 'The look of the outside walls' },
  { label: 'Outdoor kitchens',      category: 'supplier:outdoor_kitchen',    iconSrc: MATCHER_ICON('outdoor-kitchens'), blurb: 'BBQ benches and alfresco cabinets for entertaining' },
];

const PRO_CATEGORY_OPTIONS = [
  { label: 'Architect',             category: 'professional:architect',           iconSrc: MATCHER_ICON('architect'),           blurb: 'Registered SA architect for complex, design-led or heritage projects' },
  { label: 'Building Designer',     category: 'professional:building_designer',   iconSrc: MATCHER_ICON('architect'),           blurb: 'Custom home drawings and DA documentation without full architect fees' },
  { label: 'Private certifier',     category: 'professional:private_certifier',   iconSrc: MATCHER_ICON('private-certifier'),   blurb: 'Issues building consent and inspects your build against the National Construction Code' },
  { label: 'Building inspector',    category: 'professional:building_inspector',  iconSrc: MATCHER_ICON('building-inspector'),  blurb: 'An extra set of expert eyes before purchase or at handover' },
  { label: 'Finance broker',        category: 'professional:finance_broker',      iconSrc: MATCHER_ICON('finance-broker'),      blurb: 'Construction loans, refinancing and first home grants' },
  { label: 'Conveyancer',           category: 'professional:conveyancer',         iconSrc: MATCHER_ICON('conveyancer'),         blurb: 'Handles the paperwork when you buy land or settle' },
  { label: 'Town planner',          category: 'professional:town_planner',        iconSrc: MATCHER_ICON('town-planner'),        blurb: 'Sorts council approval when your block is tricky' },
  { label: 'Surveyor',              category: 'professional:surveyor',            iconSrc: MATCHER_ICON('surveyor'),            blurb: 'Measures your block - boundaries, levels, contours' },
  { label: 'Structural engineer',   category: 'professional:structural_engineer', iconSrc: MATCHER_ICON('structural-engineer'), blurb: 'Designs the bones - footings, retaining, structure' },
  { label: 'Quantity surveyor',     category: 'professional:quantity_surveyor',   iconSrc: MATCHER_ICON('quantity-surveyor'),   blurb: 'Independent cost estimates and tax depreciation reports' },
  { label: 'Property lawyer',       category: 'professional:property_lawyer',     iconSrc: MATCHER_ICON('property-lawyer'),     blurb: 'For contract disputes, easements and tricky titles' },
];

const launchGenericMatcher = (detail) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('buildpilot:open-matcher', { detail }));
};
const launchHnlMatcher = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('buildpilot:open-hnl-matcher'));
};
const launchHomeDesignsMatcher = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('buildpilot:open-home-designs-matcher'));
};

const STARTERS = {
  plan: [
    {
      key: 'builders',
      label: 'Builders',
      sub: 'Shortlist in 2 mins',
      iconSrc: TOOL_ICONS['find-builder'],
      iconAlt: 'Find a builder',
      testId: 'shortlist-starter-builders',
      onClick: () =>
        launchGenericMatcher({
          heading: 'What are you building?',
          subheading: 'Pick what you\'re building. We hand you 3 Adelaide builders worth talking to — nobody else.',
          categoryOptions: BUILDER_CATEGORY_OPTIONS,
          sourcePlacement: 'homepage-shortlist-starters-builders',
        }),
    },
    {
      key: 'floor-plans',
      label: 'Floor Plans',
      sub: 'Match a design',
      iconSrc: '/images/toolbox-icons/floor-plans-3d.png',
      iconAlt: 'Match a floor plan',
      testId: 'shortlist-starter-floor-plans',
      onClick: launchHomeDesignsMatcher,
    },
    {
      key: 'home-and-land',
      label: 'Home & Land',
      sub: 'Match a package',
      iconSrc: TOOL_ICONS['house-and-land'],
      iconAlt: 'Match a home and land package',
      testId: 'shortlist-starter-home-and-land',
      onClick: launchHnlMatcher,
    },
  ],
  support: [
    {
      key: 'trades',
      label: 'Trades',
      sub: 'Match a tradie',
      iconSrc: TOOL_ICONS['find-tradie'],
      iconAlt: 'Find a tradie',
      testId: 'shortlist-starter-trades',
      onClick: () =>
        launchGenericMatcher({
          heading: 'Find your trade',
          subheading: 'One brief. 3 SA tradies who actually pick up the phone. No spam.',
          categoryOptions: TRADE_CATEGORY_OPTIONS,
          sourcePlacement: 'homepage-shortlist-starters-trades',
        }),
    },
    {
      key: 'suppliers',
      label: 'Suppliers',
      sub: 'Match a supplier',
      iconSrc: '/images/toolbox-icons/suppliers-3d.png',
      iconAlt: 'Find a supplier',
      testId: 'shortlist-starter-suppliers',
      onClick: () =>
        launchGenericMatcher({
          heading: 'Find your supplier',
          subheading: 'One brief. 3 suppliers with real Adelaide pricing. Skip the showroom loop.',
          categoryOptions: SUPPLIER_CATEGORY_OPTIONS,
          sourcePlacement: 'homepage-shortlist-starters-suppliers',
        }),
    },
    {
      key: 'professional-services',
      label: 'Professional Services',
      sub: 'Match a professional',
      iconSrc: '/images/toolbox-icons/pro-services-3d.png',
      iconAlt: 'Find a professional',
      testId: 'shortlist-starter-professional-services',
      onClick: () =>
        launchGenericMatcher({
          heading: 'Find your professional',
          subheading: 'One brief. 3 SA pros who\'ve done exactly your job. No generalists.',
          categoryOptions: PRO_CATEGORY_OPTIONS,
          sourcePlacement: 'homepage-shortlist-starters-pros',
        }),
    },
  ],
};

const ROTATING_TYPES = [
  'builder',
  'floor plan',
  'home & land',
  'trade',
  'supplier',
  'pro service',
];
// Longest entry sets the slot width via an invisible phantom token so the
// surrounding heading words ("Shortlist a ___ in 2 minutes") never shift
// horizontally as the rotation cycles.
const LONGEST_TYPE = ROTATING_TYPES.reduce((a, b) => (b.length > a.length ? b : a), '');

const RotatingSlot = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const cycle = setInterval(() => {
      setIdx((p) => (p + 1) % ROTATING_TYPES.length);
    }, 2400);
    return () => clearInterval(cycle);
  }, []);

  return (
    <span className="bp-rot-slot" aria-live="polite" data-testid="shortlist-rotating-type">
      {/* Phantom widest token - invisible, owns the slot's width */}
      <span className="bp-rot-phantom" aria-hidden="true">{LONGEST_TYPE}</span>
      {ROTATING_TYPES.map((t, i) => (
        <span
          key={t}
          className="bp-rot-item"
          data-active={i === idx ? 'yes' : 'no'}
          aria-hidden={i === idx ? 'false' : 'true'}
        >
          {t}
        </span>
      ))}
    </span>
  );
};

const PillCard = ({ onClick, label, sub, iconSrc, iconAlt, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group relative flex items-center gap-2.5 sm:gap-4 w-full px-3 py-2.5 sm:px-5 sm:py-4
               rounded-full text-left
               bg-white border border-white
               shadow-[0_1px_2px_rgba(15,23,42,0.05),0_12px_32px_-12px_rgba(82,49,154,0.30)]
               hover:shadow-[0_2px_6px_rgba(118,75,162,0.16),0_20px_48px_-16px_rgba(118,75,162,0.50)]
               hover:-translate-y-0.5 active:translate-y-0
               transition-all duration-200 ease-out
               focus:outline-none focus:ring-2 focus:ring-white/80 focus:ring-offset-2 focus:ring-offset-transparent
               cursor-pointer"
  >
    <img
      src={iconSrc}
      alt={iconAlt}
      loading="lazy"
      aria-hidden="true"
      className="w-9 h-9 sm:w-12 sm:h-12 object-contain shrink-0
                 group-hover:scale-105 transition-transform duration-200"
    />
    <span className="flex-1 min-w-0">
      <span className="block text-[12.5px] sm:text-[15px] md:text-[16px] font-semibold text-[#1d1d1f] leading-tight truncate">
        {label}
      </span>
      <span className="hidden sm:block text-[12px] text-[#6e6e73] mt-0.5 truncate">
        {sub}
      </span>
    </span>
    <ArrowUpRight
      className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#86868b] group-hover:text-[#1d8efe]
                 group-hover:translate-x-0.5 group-hover:-translate-y-0.5
                 transition-all duration-200 shrink-0"
      strokeWidth={2.2}
    />
  </button>
);

const BrandVideoCard = () => {
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = () => setIsPlaying(true);

  return (
    <div
      className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(0,0,0,0.55)] ring-1 ring-white/10 bg-black flex-1 min-h-0"
      data-testid="shortlist-intelligence-video"
      onContextMenu={(e) => e.preventDefault()}
    >
      {!isPlaying && (
        <button
          type="button"
          onClick={handlePlay}
          aria-label="Play BuildPilot Intelligence video"
          className="absolute inset-0 z-10 cursor-pointer flex items-center justify-center group focus:outline-none bg-cover bg-center"
          style={{ backgroundImage: 'url(/videos/buildpilot-intelligence-poster.jpg)' }}
          data-testid="shortlist-intelligence-video-play"
        >
          <span className="w-14 h-14 sm:w-16 sm:h-16 gradient-blue-bg rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
            <Play className="w-6 h-6 sm:w-7 sm:h-7 text-white ml-0.5" fill="white" />
          </span>
        </button>
      )}
      {isPlaying && (
        /* 2026-02-28: swapped self-hosted MP4 for the canonical
           "How it works" video (Google Drive). Iframe extends
           symmetrically up + down past the container - `overflow-hidden`
           clips the Drive top gradient (with pop-out button) at the
           top and the controls bar at the bottom. Video content
           stays perfectly centered (16:9 letterbox inside iframe)
           and horizontal edges are flush - nothing gets side-cropped. */
        <div className="absolute inset-0 aspect-video lg:aspect-auto w-full h-full">
          <iframe
            src="https://drive.google.com/file/d/102VAEri64zksOqtTHac2ixwp56LrhF2x/preview?rm=minimal&autoplay=1"
            title="How the BuildPilot matcher works"
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
            className="absolute left-0 border-0"
            style={{ top: '-52px', width: '100%', height: 'calc(100% + 104px)' }}
            data-testid="shortlist-intelligence-video-iframe"
          />
        </div>
      )}
    </div>
  );
};

const ShortlistStartersSection = () => {
  return (
    <section
      className="relative overflow-hidden py-9 sm:py-20 lg:py-28"
      data-testid="shortlist-starters-section"
      aria-labelledby="shortlist-starters-heading"
    >
      {/* Background: BuildPilot Intelligence canvas + extended white fades
          on top and bottom for a much softer hand-off into adjacent sections. */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute inset-0 bp-intel-canvas" />
        <div className="absolute inset-0 bp-intel-grain opacity-[0.05] mix-blend-overlay" />
        {/* Very long, very soft 4-stop fades. Eases for nearly half the
            section height so the transition into the white sections
            above/below is barely perceptible. */}
        <div className="absolute inset-x-0 top-0 h-72 sm:h-80 bp-intel-fade-top" />
        <div className="absolute inset-x-0 bottom-0 h-72 sm:h-80 bp-intel-fade-bottom" />
      </div>

      <div className="relative max-w-[1200px] mx-auto px-5 sm:px-6">
        {/* Rotating heading - "Shortlist a {type}" - the rotating word
            picks up the CoPilot blue gradient (same one used for "CoPilot"
            in the homepage hero). No "in 2 minutes" suffix. */}
        <h2
          id="shortlist-starters-heading"
          className="text-center text-[#0a2540] text-[22px] sm:text-[32px] lg:text-[40px]
                     font-semibold leading-[1.2] tracking-[-0.02em]
                     mb-6 sm:mb-14 lg:mb-16"
        >
          <span>Shortlist a </span>
          <RotatingSlot />
        </h2>

        {/* Project-agnostic supporting line - signals the full home-project
            breadth (not just builders) so renovators / improvers see
            themselves here too. */}
        <p
          className="text-center text-[#0a2540] text-[14px] sm:text-[17px] leading-[1.5]
                     max-w-[560px] mx-auto -mt-3 sm:-mt-10 lg:-mt-12 mb-6 sm:mb-14 lg:mb-16"
          data-testid="shortlist-starters-subtitle"
        >
          Whatever you're working on, we'll line up the right people, products and tools.
        </p>

        {/* Two-column body: pills left, video right.
            Pills are sized to match the video card's height so the two
            columns visually line up on desktop. Mobile stacks: pills → video.
            On mobile the pill grid stays 2-column (denser, less scroll). */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-8 lg:gap-10 items-stretch">
          {/* LEFT - 6 pills as a 2x3 grid that fills the column height
              to mirror the right-hand video card. */}
          <div className="lg:col-span-7 order-1 flex">
            <div
              className="grid grid-cols-2 gap-2.5 sm:gap-3 w-full lg:auto-rows-fr"
              data-testid="shortlist-starters-grid"
            >
              {[...STARTERS.plan, ...STARTERS.support].map(({ key, ...rest }) => (
                <PillCard key={key} {...rest} />
              ))}
            </div>
          </div>

          {/* RIGHT - BuildPilot Intelligence video, top-aligned with pills */}
          <div className="lg:col-span-5 order-2 flex flex-col">
            <BrandVideoCard />
            <p className="mt-3 text-center text-[12.5px] text-[#5a6b7f]" data-testid="shortlist-starters-video-caption">
              How it works · see the 2-minute shortlist in action
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShortlistStartersSection;
